import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs";

import { CoreService } from "../../../services/core.service";

interface MetaAsset {
  id: string;
  name: string | null;
  tasks: string[];
  instagram_business_account_id: string | null;
  instagram_username: string | null;
  usable: boolean;
}

interface MetaAssetsResponse {
  pages: MetaAsset[];
  site_id: number;
  expired: boolean;
}

interface SiteOption {
  id: number;
  domain: string;
}

interface MetaConnectionStatus {
  site_id: number;
  connection_status: string;
  connected: boolean;
  facebook_page_id: string | null;
  facebook_page_name: string | null;
  instagram_business_account_id: string | null;
  instagram_username: string | null;
  connected_at: string | null;
  last_validated_at: string | null;
}

@Component({
  selector: "publio-meta-connection",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: "./publio-meta-connection.component.html",
  styleUrl: "./publio-meta-connection.component.scss",
})
export class PublioMetaConnectionComponent
  implements OnInit, OnChanges {

  @Input() companyID: string | null = null;

  sites: SiteOption[] = [];
  pages: MetaAsset[] = [];

  selectedSiteId: number | null = null;
  selectedPageId = "";

  oauthSession: string | null = null;
  oauthError = false;

  loadingConnect = false;
  loadingAssets = false;
  confirming = false;
  connected = false;
  loadingStatus = false;

  connectionStatus: MetaConnectionStatus | null = null;

  errorMessage = "";

  constructor(
    private coreService: CoreService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const oauthResult =
      this.route.snapshot.queryParamMap.get("fb_oauth");

    const session =
      this.route.snapshot.queryParamMap.get("session");

    if (oauthResult === "error") {
      this.oauthError = true;
      this.errorMessage =
        "Meta authorization was not completed.";
      return;
    }

    if (oauthResult === "success" && session) {
      this.oauthSession = session;
      this.loadAssets(session);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["companyID"] &&
      this.companyID
    ) {
      this.loadSites();
    }
  }

  onSiteChange(siteId: number | null): void {
    this.selectedSiteId = siteId;
    this.connectionStatus = null;
    this.connected = false;
    this.errorMessage = "";

    if (siteId) {
      this.loadConnectionStatus(siteId);
    }
  }

  connect(): void {
    if (!this.selectedSiteId) {
      this.errorMessage =
        "Select a Publio Site before connecting Meta.";
      return;
    }

    this.loadingConnect = true;
    this.errorMessage = "";

    this.coreService.getData(
      "/integrations/meta/connect",
      {
        site_id: this.selectedSiteId,
      }
    )
      .pipe(
        finalize(() => {
          this.loadingConnect = false;
        })
      )
      .subscribe({
        next: (response: {
          authorization_url?: string;
        }) => {
          const authorizationUrl =
            response.authorization_url;

          if (!authorizationUrl) {
            this.errorMessage =
              "Meta authorization URL was not returned.";
            return;
          }

          window.location.assign(authorizationUrl);
        },
        error: () => {
          this.errorMessage =
            "Unable to start the Meta connection.";
        },
      });
  }

  confirm(): void {
    if (!this.oauthSession || !this.selectedPageId) {
      this.errorMessage =
        "Select an available Facebook Page.";
      return;
    }

    this.confirming = true;
    this.errorMessage = "";

    this.coreService.postData(
      "integrations/meta/confirm",
      {
        session: this.oauthSession,
        facebook_page_id: this.selectedPageId,
      }
    )
      .pipe(
        finalize(() => {
          this.confirming = false;
        })
      )
      .subscribe({
        next: () => {
          this.connected = true;
          this.pages = [];
          this.oauthSession = null;
          this.selectedPageId = "";

          if (this.selectedSiteId) {
            this.loadConnectionStatus(
              this.selectedSiteId
            );
          }

          this.coreService.showSnackbar(
            "Meta connection saved successfully."
          );

          this.clearOAuthQueryParameters();
        },
        error: () => {
          this.errorMessage =
            "Unable to save the selected Meta connection.";
        },
      });
  }

  trackByPageId(
    _index: number,
    page: MetaAsset
  ): string {
    return page.id;
  }

  private loadAssets(session: string): void {
    this.loadingAssets = true;
    this.errorMessage = "";

    this.coreService.getData(
      "/integrations/meta/assets",
      {
        session,
      }
    )
      .pipe(
        finalize(() => {
          this.loadingAssets = false;
        })
      )
      .subscribe({
        next: (response: MetaAssetsResponse) => {
          this.pages = (response.pages || [])
            .filter((page) => page.usable);

          this.selectedSiteId = response.site_id || null;

          if (!this.pages.length) {
            this.errorMessage =
              "No usable Facebook Page with a linked " +
              "Instagram Professional account was found.";
          }
        },
        error: () => {
          this.errorMessage =
            "The Meta authorization session expired " +
            "or could not be loaded.";
        },
      });
  }

  private loadSites(): void {
    const companyId = Number(this.companyID);

    if (!companyId) {
      this.sites = [];
      this.selectedSiteId = null;
      return;
    }

    this.coreService.getSites(companyId)
      .subscribe({
        next: (response: {
          siteDomains?: Array<{
            ID: number;
            Domain: string | null;
            isApproved: string;
          }>;
        }) => {
          this.sites = (response.siteDomains || [])
            .filter(
              (site) =>
                site.isApproved === "Approved" &&
                site.Domain !== "default"
            )
            .map((site) => ({
              id: Number(site.ID),
              domain:
                site.Domain || `Site #${site.ID}`,
            }));

          if (
            !this.selectedSiteId &&
            this.sites.length === 1
          ) {
            this.selectedSiteId = this.sites[0].id;
            this.loadConnectionStatus(
              this.selectedSiteId
            );
          }
        },
        error: () => {
          this.sites = [];
          this.errorMessage =
            "Unable to load the available Publio Sites.";
        },
      });
  }

  private loadConnectionStatus(
    siteId: number
  ): void {
    this.loadingStatus = true;
    this.connectionStatus = null;

    this.coreService.getData(
      `/integrations/meta/connections/${siteId}`
    )
      .pipe(
        finalize(() => {
          this.loadingStatus = false;
        })
      )
      .subscribe({
        next: (status: MetaConnectionStatus) => {
          this.connectionStatus = status;
          this.connected = status.connected;
        },
        error: () => {
          this.connectionStatus = null;
          this.errorMessage =
            "Unable to load the Meta connection status.";
        },
      });
  }

  private clearOAuthQueryParameters(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        fb_oauth: null,
        session: null,
      },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }
}
